// QuotationsScreen.jsx - WITH PDF LOADING
import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  TextField,
  MenuItem,
  IconButton,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  Chip,
  alpha,
  Popover,
  useTheme,
  InputAdornment,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
} from "@mui/material";
import {
  RemoveRedEyeRounded,
  ArrowBack,
  ArrowForward,
  FilterList,
  Clear,
  Today,
  Event,
  CalendarToday,
  ViewList,
  Edit,
  Assignment,
  Visibility,
  Payment,
  Description,
  PictureAsPdf,
  Receipt,
  CheckCircle,
  Pending,
  Warning,
  AttachMoney,
  AccountBalance,
  Search,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useThemeToggle } from "../context/ThemeContext";
import CustomAlert from "../components/customAlert";
import { url } from "../constants/url";

const QuotationsScreen = ({ userData }) => {
  const theme = useTheme();
  const { mode } = useThemeToggle();
  const navigate = useNavigate();

  const UID = userData?._id;
  const CID = userData?.companyAdded[0]?.companyId;
  const userRole = userData?.role;

  const [quotationsData, setQuotationsData] = useState([]);
  const [filteredQuotations, setFilteredQuotations] = useState([]);
  const [companies, setCompanies] = useState([]);

  // Filter states - SET TODAY AS DEFAULT
  const [dateFilter, setDateFilter] = useState("today");
  const [quotationStatusFilter, setQuotationStatusFilter] = useState("all");
  const [companyFilter, setCompanyFilter] = useState("all");
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);

  const [tempFilters, setTempFilters] = useState({
    status: "all",
    company: "all",
  });

  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split("T")[0]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);

  // PDF Loading state - ADD THIS
  const [pdfLoading, setPdfLoading] = useState(null); // Track which quotation is loading

  // Alert states
  const [alert, setAlert] = useState({
    open: false,
    severity: "success",
    message: "",
  });

  // Quotation statistics - UPDATED with amount calculations
  const [quotationStats, setQuotationStats] = useState({
    shared: 0,
    approved: 0,
    rejected: 0,
    revised: 0,
    total: 0,
    totalQuotationAmount: 0,
    totalApprovedAmount: 0,
    totalReceivedAmount: 0,
    totalPendingAmount: 0,
    totalOverdueAmount: 0
  });

  // Status display mapping for quotations - FIXED MAPPING
  const statusDisplayMap = {
    // Quotation-specific statuses
    quotation_shared: "Quotation Shared",
    quotation_approved: "Quotation Approved",
    quotation_revised: "Quotation Revised",
    quotation_rejected: "Quotation Rejected",

    // Payment statuses
    token_paid: "Token Paid",
    partially_paid: "Partially Paid",
    fully_paid: "Fully Paid",
    overdue: "Overdue",
    pending: "Payment Pending",

    // Other statuses
    negotiation: "Negotiation",
    sent: "Sent",
    approved: "Approved",
    rejected: "Rejected",
    expired: "Expired",
    closed: "Closed",
    dead: "Dead"
  };

  // Payment status mapping
  const paymentStatusMap = {
    pending: "Pending",
    token_paid: "Token Paid",
    partially_paid: "Partially Paid",
    fully_paid: "Fully Paid",
    overdue: "Overdue"
  };

  // Color palette for cards
  const COLORS = {
    primary: mode === 'light' ? '#1E8E3E' : '#34D399',
    success: '#10B981',
    warning: '#F59E0B',
    error: '#EF4444',
    info: '#3B82F6',
    purple: '#8B5CF6',
    indigo: '#4F46E5',
    cyan: '#06B6D4',
  };

  // Gradient backgrounds for cards
  const GRADIENTS = {
    primary: mode === 'light'
      ? 'linear-gradient(135deg, #1E8E3E 0%, #34A853 100%)'
      : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
    success: mode === 'light'
      ? 'linear-gradient(135deg, #10B981 0%, #059669 100%)'
      : 'linear-gradient(135deg, #34D399 0%, #10B981 100%)',
    warning: mode === 'light'
      ? 'linear-gradient(135deg, #F59E0B 0%, #D97706 100%)'
      : 'linear-gradient(135deg, #FBBF24 0%, #D97706 100%)',
    error: mode === 'light'
      ? 'linear-gradient(135deg, #EF4444 0%, #DC2626 100%)'
      : 'linear-gradient(135deg, #F87171 0%, #DC2626 100%)',
    info: mode === 'light'
      ? 'linear-gradient(135deg, #3B82F6 0%, #2563EB 100%)'
      : 'linear-gradient(135deg, #60A5FA 0%, #2563EB 100%)',
    purple: mode === 'light'
      ? 'linear-gradient(135deg, #8B5CF6 0%, #7C3AED 100%)'
      : 'linear-gradient(135deg, #A78BFA 0%, #7C3AED 100%)',
    indigo: mode === 'light'
      ? 'linear-gradient(135deg, #4F46E5 0%, #3730A3 100%)'
      : 'linear-gradient(135deg, #818CF8 0%, #3730A3 100%)',
    cyan: mode === 'light'
      ? 'linear-gradient(135deg, #06B6D4 0%, #0891B2 100%)'
      : 'linear-gradient(135deg, #22D3EE 0%, #0891B2 100%)',
  };

  // ==================== COMPACT QUOTATION CARD COMPONENT ====================
  const CompactQuotationCard = ({ title, amount, icon, gradient, description }) => (
    <Card
      sx={{
        p: 2,
        borderRadius: 2,
        background: gradient,
        color: 'white',
        cursor: 'default',
        transition: 'all 0.3s ease',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        position: 'relative',
        overflow: 'hidden',
        minWidth: '160px',
        maxWidth: '200px',
        flex: '1 1 160px',
        height: '110px',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: 'rgba(255,255,255,0.3)',
        }
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
        <Box sx={{ flex: 1 }}>
          <Typography variant="h6" sx={{
            fontWeight: 800,
            mb: 0.5,
            color: 'white',
            fontSize: '1.2rem',
            lineHeight: 1.2
          }}>
            ₹{(amount || 0).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
          </Typography>
          <Typography variant="body2" sx={{
            fontWeight: 600,
            color: 'white',
            fontSize: '0.8rem',
            lineHeight: 1.2,
            mb: 0.2
          }}>
            {title}
          </Typography>
          <Typography variant="caption" sx={{
            color: 'rgba(255,255,255,0.8)',
            fontSize: '0.65rem',
            lineHeight: 1.2
          }}>
            {description}
          </Typography>
        </Box>
        <Box sx={{
          p: 0.8,
          borderRadius: 1,
          backgroundColor: 'rgba(255,255,255,0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          {React.cloneElement(icon, { sx: { fontSize: 16, color: 'white' } })}
        </Box>
      </Box>
    </Card>
  );

  const QuotationMetrics = ({ stats }) => {
    const amountCards = [
      {
        title: "Total Quotation",
        amount: stats.totalQuotationAmount || 0,
        icon: <AttachMoney />,
        color: "primary",
        description: "All quotations value",
        gradient: GRADIENTS.primary
      },
      {
        title: "Approved Amount",
        amount: stats.totalApprovedAmount || 0,
        icon: <CheckCircle />,
        color: "success",
        description: "Approved quotations",
        gradient: GRADIENTS.success
      },
      {
        title: "Received Amount",
        amount: stats.totalReceivedAmount || 0,
        icon: <Receipt />,
        color: "info",
        description: "Received payments",
        gradient: GRADIENTS.info
      },
      {
        title: "Overdue Amount",
        amount: stats.totalOverdueAmount || 0,
        icon: <Warning />,
        color: "error",
        description: "Overdue payments",
        gradient: GRADIENTS.error
      }
    ];

    return (
      <Box sx={{ mb: 3 }}>
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'flex-start',
            gap: 1.5,
            flexWrap: 'nowrap',
            overflowX: 'auto',
            pb: 1,
            '& > *': {
              flex: '0 0 auto'
            }
          }}
        >
          {amountCards.map((card, index) => (
            <CompactQuotationCard
              key={index}
              title={card.title}
              amount={card.amount}
              icon={card.icon}
              gradient={card.gradient}
              description={card.description}
            />
          ))}
        </Box>
      </Box>
    );
  };

  // Get display status text
  // const getDisplayStatus = (quotation) => {
  //   // Use leadStatus if it exists and contains "quotation_", otherwise use status
  //   const statusToDisplay = 
  //     quotation.leadStatus && quotation.leadStatus.includes("quotation_") 
  //       ? quotation.leadStatus 
  //       : quotation.leadStatus;

  //   return statusDisplayMap[statusToDisplay] || 
  //          statusToDisplay?.charAt(0)?.toUpperCase() + 
  //          statusToDisplay?.slice(1)?.replace(/_/g, ' ') || 
  //          "Unknown";
  // };
  // Get display status text - UPDATED VERSION
  // Get display status text - CORRECTED VERSION
  const getDisplayStatus = (quotation) => {
    // console.log("DEBUG getDisplayStatus:", {
    //   quotationNumber: quotation.quotationNumber,
    //   leadStatus: quotation.leadStatus,
    //   status: quotation.status
    // });

    // Use leadStatus first, if it exists
    const statusToDisplay = quotation.leadStatus || quotation.status;

    // console.log("Status to display:", statusToDisplay);
    // console.log("Lookup in statusDisplayMap:", statusDisplayMap[statusToDisplay]);

    // Check if we have a direct mapping
    if (statusDisplayMap[statusToDisplay]) {
      return statusDisplayMap[statusToDisplay];
    }

    // If no direct mapping, format the status text
    if (statusToDisplay) {
      return statusToDisplay
        .split('_')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    }

    return "Unknown";
  };

  // Get display payment status text
  const getDisplayPaymentStatus = (paymentStatus) => {
    return paymentStatusMap[paymentStatus] || paymentStatus?.charAt(0)?.toUpperCase() + paymentStatus?.slice(1) || "Pending";
  };

  // Status color mapping for quotations - UPDATED
  const getStatusColor = (status) => {
    if (status === "sent") return alpha(theme.palette.info.main, 0.1);
    if (status === "quotation_approved") return alpha(theme.palette.success.main, 0.1);
    if (status === "quotation_revised") return alpha(theme.palette.warning.main, 0.1); // Added this
    if (status === "quotation_rejected") return alpha(theme.palette.error.main, 0.1);
    if (status === "quotation_negotiation") return alpha(theme.palette.secondary.main, 0.1);
    if (status === "expired") return alpha(theme.palette.grey[500], 0.1);
    return "inherit";
  };

  const getStatusChipColor = (status) => {
    if (status === "sent") return "info";
    if (status === "quotation_approved") return "success";
    if (status === "quotation_revised") return "warning"; // Added this
    if (status === "quotation_rejected") return "error";
    if (status === "negotiation") return "secondary";
    if (status === "expired") return "default";
    return "default";
  };

  const getPaymentStatusChipColor = (paymentStatus) => {
    if (paymentStatus === "fully_paid") return "success";
    if (paymentStatus === "partially_paid" || paymentStatus === "token_paid") return "warning";
    if (paymentStatus === "overdue") return "error";
    return "default";
  };

  // Helper functions
  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  };

  // Calculate quotation statistics - FIXED FUNCTION with proper pending amount calculation
  // const calculateQuotationStats = (quotations) => {
  //   const stats = {
  //     shared: 0,
  //     approved: 0,
  //     rejected: 0,
  //     revised: 0,
  //     total: quotations.length,
  //     totalQuotationAmount: 0,
  //     totalApprovedAmount: 0,
  //     totalReceivedAmount: 0,
  //     totalPendingAmount: 0,
  //     totalOverdueAmount: 0,
  //     totalTokenAmount: 0,
  //     totalPartiallyPaidAmount: 0,
  //     totalFullyPaidAmount: 0
  //   };

  //   quotations.forEach(quotation => {
  //     // 🚨 FIXED: Check both quotation status AND lead status
  //     const isQuotationApproved = quotation.status === "approved" || 
  //                                quotation.status === "quotation_approved" ||
  //                                quotation.leadStatus === "quotation_approved" ||
  //                                quotation.leadStatus === "approved";

  //     // Count by status - FIXED STATUS MAPPING
  //     if (quotation.status === "quotation_shared" || quotation.status === "sent" || quotation.leadStatus === "quotation_shared") {
  //       stats.shared++;
  //     }

  //     if (isQuotationApproved) {
  //       stats.approved++;
  //     }

  //     if (quotation.status === "quotation_rejected" || quotation.status === "rejected" || quotation.leadStatus === "rejected") {
  //       stats.rejected++;
  //     }

  //     if (quotation.isRevised) {
  //       stats.revised++;
  //     }

  //     // Calculate amounts properly
  //     const quotationAmount = Number(quotation.finalAmount) || Number(quotation.totalAmount) || 0;

  //     // 🚨 FIXED: Approved amount should be calculated based on approved status
  //     const approvedAmount = isQuotationApproved ? quotationAmount : 0;

  //     // Calculate received amount from actual payments
  //     let receivedAmount = 0;
  //     let tokenAmount = 0;
  //     let partiallyPaidAmount = 0;
  //     let fullyPaidAmount = 0;

  //     if (quotation.payments && Array.isArray(quotation.payments)) {
  //       quotation.payments.forEach(payment => {
  //         if (payment.paymentType === 'token' && !payment.isOverdueRecord) {
  //           tokenAmount += Number(payment.amount) || 0;
  //         } else if (payment.paymentType === 'partially_paid' && !payment.isOverdueRecord) {
  //           partiallyPaidAmount += Number(payment.amount) || 0;
  //         } else if (payment.paymentType === 'fully_paid' && !payment.isOverdueRecord) {
  //           fullyPaidAmount += Number(payment.amount) || 0;
  //         }
  //       });

  //       // SUM ALL VALID PAYMENTS (exclude overdue records)
  //       receivedAmount = quotation.payments
  //         .filter(payment => !payment.isOverdueRecord)
  //         .reduce((sum, payment) => sum + (Number(payment.amount) || 0), 0);
  //     } else {
  //       // Fallback to paidAmount field if payments array doesn't exist
  //       receivedAmount = Number(quotation.paidAmount) || 0;
  //     }

  //     // 🚨 FIXED: Pending amount calculation - Only for approved quotations
  //     let pendingAmount = 0;
  //     if (isQuotationApproved) {
  //       pendingAmount = Math.max(0, quotationAmount - receivedAmount);
  //     }

  //     stats.totalQuotationAmount += quotationAmount;
  //     stats.totalApprovedAmount += approvedAmount;
  //     stats.totalReceivedAmount += receivedAmount;
  //     stats.totalPendingAmount += pendingAmount;
  //     stats.totalTokenAmount += tokenAmount;
  //     stats.totalPartiallyPaidAmount += partiallyPaidAmount;
  //     stats.totalFullyPaidAmount += fullyPaidAmount;

  //     // Check for overdue - use payment status AND date validation
  //     if (quotation.paymentStatus === 'overdue' && pendingAmount > 0) {
  //       stats.totalOverdueAmount += pendingAmount;
  //     } else if (quotation.validUntil && pendingAmount > 0) {
  //       // Also check if validUntil date has passed
  //       const validUntil = new Date(quotation.validUntil);
  //       const today = new Date();
  //       today.setHours(0, 0, 0, 0);
  //       validUntil.setHours(0, 0, 0, 0);
  //       if (validUntil < today) {
  //         stats.totalOverdueAmount += pendingAmount;
  //       }
  //     }
  //   });

  //   // Round to 2 decimal places
  //   Object.keys(stats).forEach(key => {
  //     if (key.includes('Amount')) {
  //       stats[key] = Math.round(stats[key] * 100) / 100;
  //     }
  //   });

  //   return stats;
  // };

  // In QuotationsScreen.jsx - Update the calculateQuotationStats function
  // COMPLETE UPDATED calculateQuotationStats function
  const calculateQuotationStats = (quotations) => {
    console.log("📈 Starting stats calculation for", quotations.length, "quotations");

    const stats = {
      shared: 0,
      approved: 0,
      rejected: 0,
      revised: 0,
      total: quotations.length,
      totalQuotationAmount: 0,
      totalApprovedAmount: 0,
      totalReceivedAmount: 0,
      totalPendingAmount: 0,
      totalOverdueAmount: 0,
      totalTokenAmount: 0,
      totalPartiallyPaidAmount: 0,
      totalFullyPaidAmount: 0
    };

    quotations.forEach((quotation, index) => {
      console.log(`\n📊 Processing quotation ${index + 1}/${quotations.length}:`, {
        quotationNumber: quotation.quotationNumber,
        status: quotation.status,
        originalStatus: quotation.originalStatus,
        leadStatus: quotation.leadStatus,
        paymentStatus: quotation.paymentStatus,
        finalAmount: quotation.finalAmount,
        originalApprovedAmount: quotation.originalApprovedAmount,
        overdueAmount: quotation.overdueAmount,
        isRevised: quotation.isRevised,
      });

      // 🚨 CRITICAL FIX: Determine the REAL status for counting
      let effectiveStatus = quotation.status;
      let isEffectivelyApproved = false;

      // If quotation has originalStatus, use it (for overdue quotations)
      if (quotation.originalStatus && quotation.paymentStatus === 'overdue') {
        effectiveStatus = quotation.originalStatus;
        console.log(`🔄 Using originalStatus for overdue quotation: ${effectiveStatus}`);
      }

      // 🚨 UPDATED: Check if quotation should be counted as approved
      isEffectivelyApproved =
        // Direct status checks
        effectiveStatus === "approved" ||
        effectiveStatus === "quotation_approved" ||
        // Lead status checks
        quotation.leadStatus === "quotation_approved" ||
        quotation.leadStatus === "approved" ||
        quotation.quotationStatus === "quotation_approved" ||
        // Check if it has originalApprovedAmount (was approved before)
        (quotation.originalApprovedAmount > 0) ||
        // Special case: overdue but has originalApprovedAmount
        (quotation.paymentStatus === 'overdue' && quotation.originalApprovedAmount > 0);

      console.log("🎯 Effective status determination:", {
        effectiveStatus,
        isEffectivelyApproved,
        originalApprovedAmount: quotation.originalApprovedAmount,
        originalStatus: quotation.originalStatus,
        paymentStatus: quotation.paymentStatus,
      });

      // Count by effective status
      if (effectiveStatus === "quotation_shared" || effectiveStatus === "sent") {
        stats.shared++;
        console.log("➕ Added to shared count");
      }

      if (isEffectivelyApproved) {
        stats.approved++;
        console.log("✅ Added to approved count");
      }

      if (effectiveStatus === "quotation_rejected" || effectiveStatus === "rejected") {
        stats.rejected++;
        console.log("➕ Added to rejected count");
      }

      if (quotation.isRevised) {
        stats.revised++;
        console.log("➕ Added to revised count");
      }

      // 🚨 FIXED: Calculate amounts properly
      const quotationAmount = Number(quotation.finalAmount) || Number(quotation.totalAmount) || 0;

      // 🚨 CRITICAL: Use originalApprovedAmount if available, otherwise use quotation amount
      // In calculateQuotationStats function, update the approved amount calculation:
      let approvedAmountForCalculation = quotationAmount;

      // 🚨 USE APPROVED AMOUNT IF AVAILABLE
      if (quotation.approvedAmount && quotation.approvedAmount > 0) {
        approvedAmountForCalculation = Number(quotation.approvedAmount);
        console.log(`💰 Using lead-level approvedAmount: ${approvedAmountForCalculation}`);
      } else if (quotation.originalApprovedAmount && quotation.originalApprovedAmount > 0) {
        approvedAmountForCalculation = quotation.originalApprovedAmount;
        console.log(`💰 Using originalApprovedAmount: ${approvedAmountForCalculation}`);
      } else if (isEffectivelyApproved && quotationAmount > 0) {
        approvedAmountForCalculation = quotationAmount;
        console.log(`💰 Using quotation amount as approved: ${approvedAmountForCalculation}`);
      }

      // Calculate received amount from actual payments (excluding overdue records)
      let receivedAmount = 0;
      let tokenAmount = 0;
      let partiallyPaidAmount = 0;
      let fullyPaidAmount = 0;

      if (quotation.payments && Array.isArray(quotation.payments)) {
        quotation.payments.forEach(payment => {
          const paymentAmount = Number(payment.amount) || 0;

          if (!payment.isOverdueRecord) {
            receivedAmount += paymentAmount;

            if (payment.paymentType === 'token') {
              tokenAmount += paymentAmount;
            } else if (payment.paymentType === 'partially_paid') {
              partiallyPaidAmount += paymentAmount;
            } else if (payment.paymentType === 'fully_paid') {
              fullyPaidAmount += paymentAmount;
            }
          }
        });
      } else {
        // Fallback to paidAmount field if payments array doesn't exist
        receivedAmount = Number(quotation.paidAmount) || 0;
      }

      console.log("💰 Payment calculations:", {
        quotationAmount,
        approvedAmountForCalculation,
        receivedAmount,
        tokenAmount,
        partiallyPaidAmount,
        fullyPaidAmount,
      });

      // 🚨 FIXED: Approved amount calculation
      // Include in totalApprovedAmount if effectively approved
      if (isEffectivelyApproved) {
        stats.totalApprovedAmount += approvedAmountForCalculation;
        console.log(`💰 Added ${approvedAmountForCalculation} to totalApprovedAmount`);
      }

      stats.totalQuotationAmount += quotationAmount;
      stats.totalReceivedAmount += receivedAmount;
      stats.totalTokenAmount += tokenAmount;
      stats.totalPartiallyPaidAmount += partiallyPaidAmount;
      stats.totalFullyPaidAmount += fullyPaidAmount;

      // 🚨 FIXED: Overdue/Pending amount calculation
      // NEW FLOW: Any approved but unpaid amount is NOT overdue yet.
      // Standardized Rule: Approved + Some Payment + Remaining = Overdue
      if (isEffectivelyApproved && receivedAmount > 0) {
        const remainingAmount = Math.max(0, approvedAmountForCalculation - receivedAmount);
        if (remainingAmount > 0) {
          stats.totalOverdueAmount += remainingAmount;
          console.log(`📊 Added outstanding amount to Overdue: ${remainingAmount} (${approvedAmountForCalculation} - ${receivedAmount})`);
        }
      } else if (quotation.paymentStatus === 'overdue' && receivedAmount > 0) {
        // Fallback for explicitly marked overdue 
        const remainingAmount = Math.max(0, approvedAmountForCalculation - receivedAmount);
        if (remainingAmount > 0) {
          stats.totalOverdueAmount += remainingAmount;
        }
      }
    });

    // Round to 2 decimal places
    Object.keys(stats).forEach(key => {
      if (key.includes('Amount')) {
        stats[key] = Math.round(stats[key] * 100) / 100;
      }
    });

    console.log("\n📈 FINAL STATS CALCULATED:", {
      total: stats.total,
      shared: stats.shared,
      approved: stats.approved,
      rejected: stats.rejected,
      revised: stats.revised,
      totalQuotationAmount: stats.totalQuotationAmount,
      totalApprovedAmount: stats.totalApprovedAmount,
      totalReceivedAmount: stats.totalReceivedAmount,
      totalPendingAmount: stats.totalPendingAmount,
      totalOverdueAmount: stats.totalOverdueAmount,
    });

    return stats;
  };
  // Apply filters to quotations data - UPDATED with quotation_revised
  // Apply filters to quotations data - FIXED VERSION
  // const applyFilters = (quotations) => {
  //   // console.log("🔄 Starting filter application...", {
  //   //   totalQuotations: quotations.length,
  //   //   statusFilter: quotationStatusFilter,
  //   //   companyFilter: companyFilter,
  //   //   dateFilter: dateFilter,
  //   //   selectedDate: selectedDate
  //   // });

  //   let filtered = [...quotations];

  //   // Apply status filter - FIXED STRICT MATCHING
  //   if (quotationStatusFilter !== "all") {
  //     console.log("🔍 Filtering by status:", quotationStatusFilter);

  //     filtered = filtered.filter(quotation => {
  //       // Get the actual status from the quotation data
  //       const actualStatus = quotation.leadStatus || quotation.status;
  //       console.log(`Checking quotation ${quotation.quotationNumber}:`, {
  //         actualStatus,
  //         filter: quotationStatusFilter,
  //         leadStatus: quotation.leadStatus,
  //         status: quotation.status
  //       });

  //       let statusMatch = false;

  //       // Handle the mapping between filter values and actual status values
  //       switch (quotationStatusFilter) {
  //         case "quotation_shared":
  //           // Only match if leadStatus is "quotation_shared" or status includes "shared"
  //           statusMatch = 
  //             actualStatus === "quotation_shared" ||
  //             (quotation.leadStatus && quotation.leadStatus.includes("shared")) ||
  //             quotation.status === "quotation_shared";
  //           break;
  //         case "quotation_approved":
  //           // Only match if leadStatus is "quotation_approved" or status includes "approved"
  //           statusMatch = 
  //             actualStatus === "quotation_approved" ||
  //             actualStatus === "approved" ||
  //             (quotation.leadStatus && quotation.leadStatus.includes("approved")) ||
  //             quotation.status === "quotation_approved" ||
  //             quotation.status === "approved";
  //           break;
  //         case "quotation_rejected":
  //           statusMatch = 
  //             actualStatus === "quotation_rejected" ||
  //             actualStatus === "rejected" ||
  //             (quotation.leadStatus && quotation.leadStatus.includes("rejected")) ||
  //             quotation.status === "quotation_rejected" ||
  //             quotation.status === "rejected";
  //           break;
  //         case "quotation_revised":
  //           // For revised quotations, check isRevised flag explicitly
  //           statusMatch = 
  //             quotation.isRevised === true ||
  //             actualStatus === "quotation_revised" || 
  //             actualStatus === "revised" ||
  //             (quotation.leadStatus && quotation.leadStatus.includes("revised"));
  //           break;
  //         case "negotiation":
  //           statusMatch = 
  //             actualStatus === "negotiation" ||
  //             (quotation.leadStatus && quotation.leadStatus.includes("negotiation"));
  //           break;
  //         case "expired":
  //           statusMatch = 
  //             actualStatus === "expired" ||
  //             (quotation.leadStatus && quotation.leadStatus.includes("expired"));
  //           break;
  //         default:
  //           // For any other status, do direct comparison
  //           statusMatch = actualStatus === quotationStatusFilter;
  //       }

  //       console.log(`Result: ${statusMatch ? "MATCH" : "NO MATCH"}`);
  //       return statusMatch;
  //     });

  //     console.log(`✅ After status filter (${quotationStatusFilter}):`, filtered.length);
  //   }

  //   // Apply company filter (for superAdmin only)
  //   if (userRole === "superAdmin" && companyFilter !== "all") {
  //     console.log("🔍 Filtering by company:", companyFilter);

  //     filtered = filtered.filter(quotation => {
  //       const companyMatch = 
  //         quotation.company?._id === companyFilter || 
  //         quotation.companyId === companyFilter;

  //       return companyMatch;
  //     });

  //     console.log(`✅ After company filter:`, filtered.length);
  //   }

  //   // Apply date filter
  //   if (dateFilter !== "all") {
  //     console.log("🔍 Filtering by date:", dateFilter, selectedDate);

  //     const today = new Date();
  //     today.setHours(0, 0, 0, 0);

  //     filtered = filtered.filter(quotation => {
  //       if (!quotation.sentDate) return false;

  //       const quotationDate = new Date(quotation.sentDate);
  //       quotationDate.setHours(0, 0, 0, 0);

  //       let dateMatch = false;

  //       switch (dateFilter) {
  //         case "today":
  //           dateMatch = quotationDate.getTime() === today.getTime();
  //           break;
  //         case "yesterday":
  //           const yesterday = new Date(today);
  //           yesterday.setDate(yesterday.getDate() - 1);
  //           dateMatch = quotationDate.getTime() === yesterday.getTime();
  //           break;
  //         case "tomorrow":
  //           const tomorrow = new Date(today);
  //           tomorrow.setDate(tomorrow.getDate() + 1);
  //           dateMatch = quotationDate.getTime() === tomorrow.getTime();
  //           break;
  //         case "custom":
  //           if (!selectedDate) {
  //             dateMatch = true;
  //           } else {
  //             const customDate = new Date(selectedDate);
  //             customDate.setHours(0, 0, 0, 0);
  //             dateMatch = quotationDate.getTime() === customDate.getTime();
  //           }
  //           break;
  //         default:
  //           dateMatch = true;
  //       }

  //       return dateMatch;
  //     });

  //     console.log(`✅ After date filter (${dateFilter}):`, filtered.length);
  //   }

  //   console.log("🎯 Final filtered count:", filtered.length);
  //   return filtered;
  // };
  const applyFilters = (quotations, filters = {}) => {
    const {
      status = quotationStatusFilter,
      company = companyFilter,
      date = dateFilter,
      dateVal = selectedDate
    } = filters;

    let filtered = [...quotations];

    // 1. Apply company filter first (Global)
    if (userRole === "superAdmin" && company !== "all") {
      filtered = filtered.filter(quotation =>
        quotation.company?._id === company || quotation.companyId === company
      );
    }

    // 2. Apply date filter (Global)
    if (date !== "all") {
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      filtered = filtered.filter(quotation => {
        if (!quotation.sentDate) return false;

        const quotationDate = new Date(quotation.sentDate);
        quotationDate.setHours(0, 0, 0, 0);

        switch (date) {
          case "today":
            return quotationDate.getTime() === today.getTime();
          case "yesterday":
            const yesterday = new Date(today);
            yesterday.setDate(yesterday.getDate() - 1);
            return quotationDate.getTime() === yesterday.getTime();
          case "tomorrow":
            const tomorrow = new Date(today);
            tomorrow.setDate(tomorrow.getDate() + 1);
            return quotationDate.getTime() === tomorrow.getTime();
          case "custom":
            if (!dateVal) return true;
            const customDate = new Date(dateVal);
            customDate.setHours(0, 0, 0, 0);
            return quotationDate.getTime() === customDate.getTime();
          default:
            return true;
        }
      });
    }

    // This is our "Global Filtered" base for stats
    const globalFiltered = [...filtered];

    // 3. Apply status filter (Local for table only)
    if (status !== "all") {
      filtered = filtered.filter(quotation => {
        const leadStatuses = new Set([
          quotation.status,
          quotation.leadStatus,
          quotation.paymentStatus,
          quotation.quotationStatus
        ].filter(s => s && s !== 'none'));

        let statusMatch = leadStatuses.has(status);

        if (!statusMatch) {
          if (status === 'quotation_shared') {
            statusMatch = leadStatuses.has('sent') || leadStatuses.has('quotation_shared');
          } else if (status === 'quotation_approved') {
            statusMatch = leadStatuses.has('approved') || leadStatuses.has('quotation_approved');
          } else if (status === 'quotation_rejected') {
            statusMatch = leadStatuses.has('rejected') || leadStatuses.has('quotation_rejected');
          } else if (status === 'overdue') {
            statusMatch = leadStatuses.has('overdue') || leadStatuses.has('payment_overdue');
          } else if (status === 'quotation_revised') {
            statusMatch = quotation.isRevised === true || leadStatuses.has('revised') || leadStatuses.has('quotation_revised');
          }
        }
        return statusMatch;
      });
    }

    // 4. Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(quotation => {
        const clientMatch = quotation.clientName && quotation.clientName.toLowerCase().includes(query);
        const numberMatch = quotation.quotationNumber && quotation.quotationNumber.toLowerCase().includes(query);
        const contactMatch = quotation.contactNo && quotation.contactNo.includes(query);

        // Status matching - search across multiple status-related fields
        const statusFields = [
          quotation.status,
          quotation.leadStatus,
          quotation.paymentStatus,
          quotation.quotationStatus
        ].filter(Boolean).map(s => s.toLowerCase().replace(/_/g, ' '));

        const statusMatch = statusFields.some(s => s.includes(query));

        return clientMatch || numberMatch || contactMatch || statusMatch;
      });
    }

    return { filtered, globalFiltered };
  };
  // Reset stats function
  const resetStats = () => {
    setQuotationStats({
      shared: 0,
      approved: 0,
      rejected: 0,
      revised: 0,
      total: 0,
      totalQuotationAmount: 0,
      totalApprovedAmount: 0,
      totalReceivedAmount: 0,
      totalPendingAmount: 0,
      totalOverdueAmount: 0,
      totalTokenAmount: 0,
      totalPartiallyPaidAmount: 0,
      totalFullyPaidAmount: 0
    });
  };

  // Fetch quotations data
  const fetchQuotations = async () => {
    try {
      setLoading(true);

      let fetchUrl = `${url}/api/leads/fetch-quotations?userId=${UID}&role=${userRole}&includePayments=true`;

      // Always include companyId for admin
      if (userRole === "admin" && CID) {
        fetchUrl += `&companyId=${CID}`;
      } else if (userRole === "superAdmin") {
        fetchUrl += `&companyId=all`;
      }

      const response = await fetch(fetchUrl);
      const data = await response.json();

      if (data.data) {
        console.log("📊 Fetched quotations:", data.data.length);

        setQuotationsData(data.data);
        const { filtered, globalFiltered } = applyFilters(data.data);
        setFilteredQuotations(filtered);
        const calculatedStats = calculateQuotationStats(globalFiltered);
        setQuotationStats(calculatedStats);
        console.log("📈 Calculated stats (Global):", calculatedStats);
      } else {
        setQuotationsData([]);
        setFilteredQuotations([]);
        resetStats();
      }
    } catch (error) {
      console.error("❌ Error fetching quotations:", error);
      setQuotationsData([]);
      setFilteredQuotations([]);
      resetStats();
      showAlert("error", "Failed to fetch quotations");
    } finally {
      setLoading(false);
    }
  };

  // Fetch companies for superAdmin
  const fetchCompanies = async () => {
    if (userRole !== "superAdmin") return;
    try {
      const response = await fetch(`${url}/api/company/fetch-companies`);
      const data = await response.json();
      if (data.data) {
        setCompanies(data.data);
      }
    } catch (error) {
      console.error("❌ Error fetching companies:", error);
      showAlert("error", "Failed to fetch companies");
    }
  };

  // Show alert function
  const showAlert = (severity, message) => {
    setAlert({
      open: true,
      severity,
      message,
    });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, severity: "success", message: "" });
  };

  // Filter handlers - UPDATED
  const handleDateFilterChange = (filter) => {
    console.log("📅 Changing date filter to:", filter);
    setDateFilter(filter);
    if (filter === "today") {
      setSelectedDate(new Date().toISOString().split("T")[0]);
    } else if (filter === "yesterday") {
      const d = new Date();
      d.setDate(d.getDate() - 1);
      setSelectedDate(d.toISOString().split("T")[0]);
    } else if (filter === "tomorrow") {
      const d = new Date();
      d.setDate(d.getDate() + 1);
      setSelectedDate(d.toISOString().split("T")[0]);
    } else if (filter === "all") {
      setSelectedDate("");
    }
  };

  const handleQuotationStatusFilterChange = (status) => {
    console.log("📊 Changing status filter to:", status);
    setQuotationStatusFilter(status);
  };

  const handleCompanyFilterChange = (company) => {
    console.log("🏢 Changing company filter to:", company);
    setCompanyFilter(company);
  };

  // Filter popover handlers
  const handleOpenFilterPopover = (event) => {
    setTempFilters({
      status: quotationStatusFilter,
      company: companyFilter,
    });
    setFilterAnchorEl(event.currentTarget);
  };

  const handleCloseFilterPopover = () => {
    setFilterAnchorEl(null);
  };

  const handleApplyFilters = () => {
    console.log("✅ Applying filters:", tempFilters);
    setQuotationStatusFilter(tempFilters.status);
    setCompanyFilter(tempFilters.company);
    handleCloseFilterPopover();
  };

  const handleResetFilters = () => {
    setTempFilters({
      status: "all",
      company: "all",
    });
    setQuotationStatusFilter("all");
    setCompanyFilter("all");
    handleCloseFilterPopover();
  };

  const handleClearFilters = () => {
    console.log("🧹 Clearing all filters");
    setDateFilter("all");
    setQuotationStatusFilter("all");
    setCompanyFilter("all");
    setSelectedDate("");
  };

  // Clear individual filter
  const handleClearFilter = (filterType) => {
    console.log("🧹 Clearing filter:", filterType);
    switch (filterType) {
      case 'date':
        setDateFilter("all");
        setSelectedDate("");
        break;
      case 'status':
        setQuotationStatusFilter("all");
        break;
      case 'company':
        setCompanyFilter("all");
        break;
      default:
        break;
    }
  };

  // Function to open PDF - UPDATED FOR COMBINED PDF
  const handleOpenPDF = async (quotation) => {
    try {
      setPdfLoading(quotation._id); // Start loading for this quotation

      if (!quotation.leadId) {
        showAlert("error", "No lead associated with this quotation");
        setPdfLoading(null);
        return;
      }

      console.log("📄 Generating PDF for quotation:", quotation.quotationNumber);

      // Use preview-combined for all cases, as it handles single service fallback too
      const res = await fetch(`${url}/api/templates/preview-combined`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          leadId: quotation.leadId,
          quotationId: quotation._id,
          userId: UID
        }),
      });

      if (!res.ok) {
        const errorText = await res.text();
        throw new Error(errorText || "Failed to generate PDF");
      }

      const blob = await res.blob();

      if (blob.size === 0) {
        throw new Error("Generated PDF is empty");
      }

      const pdfUrl = window.URL.createObjectURL(blob);
      const newWindow = window.open(pdfUrl, "_blank");

      if (!newWindow) {
        showAlert("warning", "Popup blocked! Please allow popups for this site to view the PDF.");
      }

    } catch (err) {
      console.error("❌ PDF generation error:", err);
      showAlert("error", `Error generating PDF: ${err.message}`);
    } finally {
      setPdfLoading(null); // Stop loading regardless of success/error
    }
  };

  // Apply filters when filter states change - ADDED DEPENDENCIES
  useEffect(() => {
    if (quotationsData.length > 0) {
      console.log("🔄 Re-applying filters due to state change:", {
        dateFilter,
        quotationStatusFilter,
        companyFilter,
        selectedDate
      });
      const { filtered, globalFiltered } = applyFilters(quotationsData);
      setFilteredQuotations(filtered);
      const calculatedStats = calculateQuotationStats(globalFiltered);
      setQuotationStats(calculatedStats);
      console.log("✅ Final filtered data:", filtered.length, "quotations");
    }
  }, [dateFilter, quotationStatusFilter, companyFilter, selectedDate, searchQuery, quotationsData]);

  useEffect(() => {
    fetchQuotations();
    if (userRole === "superAdmin") {
      fetchCompanies();
    }
  }, [UID, userRole, CID]);

  const primaryColor = theme.palette.primary.main;
  const redcolor = theme.palette.error.main;
  const greencolor = theme.palette.success.main;
  const bluecolor = theme.palette.info.main;

  // Amount Cards Data - UPDATED with premium styling
  const amountCards = [
    {
      title: "Total Quotation",
      amount: quotationStats.totalQuotationAmount,
      icon: <AttachMoney />,
      color: "primary",
      description: "All quotations value",
      bgColor: alpha(theme.palette.primary.main, 0.05),
      borderColor: theme.palette.primary.main,
      textColor: theme.palette.primary.main
    },
    {
      title: "Approved Amount",
      amount: quotationStats.totalApprovedAmount,
      icon: <CheckCircle />,
      color: "success",
      description: "Approved quotations",
      bgColor: alpha(theme.palette.success.main, 0.05),
      borderColor: theme.palette.success.main,
      textColor: theme.palette.success.main
    },
    {
      title: "Received Amount",
      amount: quotationStats.totalReceivedAmount,
      icon: <Receipt />,
      color: "info",
      description: "Total received payments",
      bgColor: alpha(theme.palette.info.main, 0.05),
      borderColor: theme.palette.info.main,
      textColor: theme.palette.info.main
    },
    {
      title: "Overdue Amount",
      amount: quotationStats.totalOverdueAmount,
      icon: <Warning />,
      color: "error",
      description: "Approved but Unpaid",
      bgColor: alpha(theme.palette.error.main, 0.05),
      borderColor: theme.palette.error.main,
      textColor: theme.palette.error.main
    }
  ];

  return (
    <>
      <Box
        sx={{
          minHeight: "100vh",
          width: "100%",
          bgcolor: "background.default",
          color: "text.primary",
          overflow: "auto",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {/* Fixed Header Section */}
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 1000,
            bgcolor: "background.default",
            pb: 2,
            borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
            mb: 2,
          }}
        >
          {/* Top Row - Filter and Active Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
              gap: 2,
            }}
          >
            {/* Left Side - Filter Button and Active Filters */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2, flex: 1 }}>
              <Button
                variant="contained"
                startIcon={<FilterList />}
                onClick={handleOpenFilterPopover}
                sx={{
                  bgcolor: "transparent",
                  boxShadow: "none",
                  border: "none",
                  color: primaryColor,
                  "&:hover": {
                    bgcolor: alpha(primaryColor, 0.08),
                    boxShadow: "none",
                    border: "none",
                  },
                  minWidth: "auto",
                  px: 2.3,
                  height: "38px",
                  textTransform: "none",
                  fontWeight: 500,
                  fontSize: "15px",
                }}
              >
                FILTERS
              </Button>

              {/* Active Filters Chips */}
              {(companyFilter !== "all" || dateFilter !== "all" || quotationStatusFilter !== "all") && (
                <Box sx={{ display: "flex", alignItems: "center", gap: 1, flexWrap: "wrap" }}>
                  {/* Date Filter Chip */}
                  {dateFilter !== "all" && (
                    <Chip
                      icon={
                        dateFilter === "custom" ? <Event /> :
                          dateFilter === "today" ? <Today /> :
                            dateFilter === "yesterday" ? <ArrowBack /> :
                              dateFilter === "tomorrow" ? <ArrowForward /> :
                                <ViewList />
                      }
                      label={
                        dateFilter === "custom" ? `Custom: ${selectedDate}` :
                          dateFilter === "today" ? "Today" :
                            dateFilter === "yesterday" ? "Yesterday" :
                              dateFilter === "tomorrow" ? "Tomorrow" :
                                "All Quotations"
                      }
                      size="medium"
                      onDelete={() => handleClearFilter('date')}
                      sx={{ color: "success.main", fontWeight: 700, height: "32px" }}
                    />
                  )}


                  {/* Status Filter Chip */}
                  {quotationStatusFilter !== "all" && (
                    <Chip
                      label={`Status: ${statusDisplayMap[quotationStatusFilter] ||
                        quotationStatusFilter.charAt(0).toUpperCase() +
                        quotationStatusFilter.slice(1).replace(/_/g, ' ')}`}
                      size="medium"
                      onDelete={() => handleClearFilter('status')}
                      sx={{ color: "info.main", fontWeight: 700, height: "32px" }}
                    />
                  )}

                  {/* Company Filter Chip */}
                  {companyFilter !== "all" && userRole === "superAdmin" && (
                    <Chip
                      label={`Company: ${companies.find((c) => c._id === companyFilter)?.companyName ||
                        companies.find((c) => c._id === companyFilter)?.name ||
                        "Unknown"
                        }`}
                      size="medium"
                      onDelete={() => handleClearFilter('company')}
                      sx={{ color: "warning.main", fontWeight: 700, height: "32px" }}
                    />
                  )}

                  {/* Clear All Filters */}
                  <Button
                    size="small"
                    startIcon={<Clear />}
                    onClick={handleClearFilters}
                    sx={{
                      color: "error.main",
                      fontSize: "0.75rem",
                      fontWeight: 500,
                      textTransform: "none",
                      "&:hover": {
                        backgroundColor: "transparent",
                        textDecoration: "underline",
                      },
                      height: "32px",
                    }}
                  >
                    Clear All
                  </Button>
                </Box>
              )}
            </Box>

            {/* Right Side - Search Bar */}
            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
              <TextField
                placeholder="Search quotations..."
                size="small"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                sx={{
                  width: "250px",
                  "& .MuiInputBase-root": {
                    height: 38,
                    bgcolor: alpha(theme.palette.background.paper, 0.8),
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 8px",
                    fontSize: "0.875rem",
                  },
                }}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Search sx={{ fontSize: 18, color: "text.secondary" }} />
                    </InputAdornment>
                  ),
                  endAdornment: searchQuery && (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setSearchQuery("")}
                        sx={{ p: 0.5 }}
                      >
                        <Clear sx={{ fontSize: 16 }} />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Box>
          </Box>

          {/* Financial Overview - NEW */}

          {/* Second Row - Stats and Date Filters */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexWrap: "nowrap",
              gap: 1,
              mb: 2,
            }}
          >
            {/* Left Side - Quotation Statistics */}
            <Box sx={{
              display: "flex",
              gap: 0.5,
              flexWrap: "nowrap",
              alignItems: "center",
              flex: 1, // Take available space
            }}>
              <Chip label={`Total: ${quotationStats.total}`} variant="outlined" color="primary" size="small" sx={{ fontWeight: "bold" }} />
              <Chip label={`Shared: ${quotationStats.shared}`} variant="filled" color="info" size="small" sx={{ fontWeight: "bold" }} />
              <Chip label={`Approved: ${quotationStats.approved}`} variant="filled" color="success" size="small" sx={{ fontWeight: "bold" }} />
              <Chip label={`Rejected: ${quotationStats.rejected}`} variant="filled" color="error" size="small" sx={{ fontWeight: "bold" }} />
              <Chip label={`Revised: ${quotationStats.revised}`} variant="filled" color="warning" size="small" sx={{ fontWeight: "bold" }} />
            </Box>

            {/* Right Side - Date Filters */}
            <Box sx={{
              display: "flex",
              gap: 0.5,
              flexWrap: "nowrap",
              alignItems: "center",
              flexShrink: 0,
            }}>
              {[
                { value: "all", label: "All", icon: <ViewList /> },
                { value: "yesterday", label: "Yesterday", icon: <ArrowBack /> },
                { value: "today", label: "Today", icon: <Today /> },
                { value: "tomorrow", label: "Tomorrow", icon: <ArrowForward /> },
              ].map((filter) => (
                <Chip
                  key={filter.value}
                  icon={filter.icon}
                  label={filter.label}
                  variant={dateFilter === filter.value ? "filled" : "outlined"}
                  color={dateFilter === filter.value ? "primary" : "default"}
                  onClick={() => handleDateFilterChange(filter.value)}
                  size="small" // Smaller chips
                  sx={{
                    ...(dateFilter === filter.value && {
                      bgcolor: "primary.main",
                      "&:hover": { bgcolor: alpha(primaryColor, 0.9) },
                    }),
                  }}
                />
              ))}

              {/* Date Picker */}
              <TextField
                type="date"
                value={selectedDate}
                onChange={(e) => {
                  setSelectedDate(e.target.value);
                  setDateFilter("custom");
                }}
                size="small"
                sx={{
                  width: "120px",
                  "& .MuiInputBase-root": {
                    height: 32, // Smaller height
                  },
                  "& .MuiInputBase-input": {
                    padding: "6px 8px",
                    fontSize: "0.8rem",
                  },
                }}
              />
            </Box>
          </Box>
          {/* Amount Cards Section - UPDATED with premium styling and space-evenly */}
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between', // Changed from space-evenly
              gap: 2,
              mb: 1,
              flexWrap: 'nowrap',
              overflowX: 'auto',
              '&::-webkit-scrollbar': { display: 'none' },
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            }}
          >
            {amountCards.map((card, index) => (
              <Card
                key={index}
                sx={{
                  flexShrink: 0, // Prevent shrinking
                  width: { xs: '100%', sm: '48%', md: '23.5%' }, // Percentage based widths like Reports.js
                  height: '110px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  background: `linear-gradient(135deg, ${card.bgColor} 0%, ${alpha(card.bgColor, 0.7)} 100%)`,
                  border: `1.5px solid ${alpha(card.borderColor, 0.2)}`, // Thinner border
                  borderRadius: 2, // Slightly smaller radius
                  boxShadow: `0 2px 12px ${alpha(card.borderColor, 0.08)}`, // Reduced shadow
                  transition: 'all 0.2s ease-in-out', // Faster transition
                  '&:hover': {
                    transform: 'translateY(-2px)', // Reduced hover effect
                    boxShadow: `0 4px 16px ${alpha(card.borderColor, 0.15)}`,
                    border: `1.5px solid ${alpha(card.borderColor, 0.4)}`,
                  },
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    right: 0,
                    height: '3px', // Thinner top accent
                    background: `linear-gradient(90deg, ${card.borderColor}, ${alpha(card.borderColor, 0.5)})`,
                  }
                }}
              >
                <CardContent sx={{
                  p: 1.5, // Reduced padding
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  '&:last-child': { pb: 1.5 } // Consistent bottom padding
                }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
                    <Box sx={{ flex: 1, mr: 0.5 }}>
                      <Typography
                        variant="h6"
                        sx={{
                          fontSize: '0.75rem', // Smaller font
                          fontWeight: '600',
                          lineHeight: 1.1, // Tighter line height
                          color: card.textColor,
                          mb: 0.25
                        }}
                      >
                        {card.title}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: alpha(card.textColor, 0.7),
                          fontSize: '0.6rem', // Smaller caption
                          display: 'block',
                          lineHeight: 1
                        }}
                      >
                        {card.description}
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        color: alpha(card.textColor, 0.7),
                        flexShrink: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.2rem' // Smaller icon
                        }
                      }}
                    >
                      {card.icon}
                    </Box>
                  </Box>

                  <Typography
                    variant="h4"
                    sx={{
                      fontWeight: 'bold',
                      color: card.textColor,
                      fontSize: '1.1rem', // Smaller amount font
                      textAlign: 'left',
                      lineHeight: 1.2
                    }}
                  >
                    ₹{card.amount?.toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}
                  </Typography>
                </CardContent>
              </Card>
            ))}
          </Box>
        </Box>

        {/* Scrollable Content Area */}
        <Box
          sx={{
            flex: 1,
            overflow: "hidden",
            "& ::-webkit-scrollbar": { display: "none" },
            "&": { "-ms-overflow-style": "none", "scrollbar-width": "none" },
          }}
        >
          {/* Quotations Table */}
          <Card
            sx={{
              bgcolor: "background.paper",
              border: `2px solid ${alpha(primaryColor, 0.2)}`,
              borderRadius: 2,
              boxShadow: "0 4px 12px 0 rgba(0, 0, 0, 0.1)",
              overflow: "hidden",
              height: "100%",
              display: "flex",
              flexDirection: "column",
            }}
          >
            <CardContent sx={{
              p: 0,
              flex: 1,
              display: "flex",
              flexDirection: "column",
              "&:last-child": { pb: 0 }
            }}>
              {loading ? (
                <Box display="flex" justifyContent="center" alignItems="center" py={4} flex={1}>
                  <CircularProgress sx={{ color: "primary.main" }} />
                </Box>
              ) : filteredQuotations.length === 0 ? (
                <Typography variant="h6" textAlign="center" py={4} color="text.secondary" flex={1}>
                  No quotations found matching your filters
                </Typography>
              ) : (
                <TableContainer
                  component={Paper}
                  sx={{
                    flex: 1,
                    overflow: "auto",
                    borderRadius: 0,
                    boxShadow: "none",
                    "& .MuiTableBody-root .MuiTableRow-root:last-child .MuiTableCell-root": {
                      borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
                    },
                  }}
                >
                  <Table stickyHeader>
                    <TableHead>
                      <TableRow>
                        {userRole === "superAdmin" && (
                          <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                            Company
                          </TableCell>
                        )}
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Client Name
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Quotation Number
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}` }}>
                          Service Interested
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`, width: "100px" }}>
                          Amount
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`, width: "95px" }}>
                          Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`, width: "90px" }}>
                          Sent Date
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`, width: "90px" }}>
                          Valid Until
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`, width: "95px" }}>
                          Payment Status
                        </TableCell>
                        <TableCell sx={{ fontWeight: "bold", bgcolor: "background.paper", borderBottom: `2px solid ${alpha(primaryColor, 0.3)}`, width: "115px", minWidth: "115px" }}>
                          Actions
                        </TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {filteredQuotations.map((quotation) => (
                        <TableRow
                          key={quotation._id}
                          sx={{
                            backgroundColor: getStatusColor(quotation.status),
                            "&:hover": {
                              backgroundColor: `${getStatusColor(quotation.status)}DD`,
                            },
                            borderBottom: `1px solid ${alpha(primaryColor, 0.1)}`,
                          }}
                        >
                          {userRole === "superAdmin" && (
                            <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                              {quotation.company?.companyName || quotation.company?.name || "N/A"}
                            </TableCell>
                          )}
                          <TableCell sx={{ fontWeight: "bold", borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            {quotation.clientName}
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontWeight: "bold" }}>
                                {quotation.quotationNumber}
                              </Typography>
                              {quotation.isRevised && (
                                <Chip
                                  label={`Revision ${quotation.revisionCount || 1}`}
                                  size="small"
                                  color="warning"
                                  variant="outlined"
                                />
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {quotation.serviceInterested && quotation.serviceInterested.length > 0 ? (
                                quotation.serviceInterested.map((s, idx) => (
                                  <Chip
                                    key={s._id || idx}
                                    label={s.service}
                                    size="small"
                                    variant="outlined"
                                    sx={{
                                      fontSize: '0.65rem',
                                      height: '20px',
                                      borderColor: alpha(primaryColor, 0.3),
                                      color: theme.palette.text.primary
                                    }}
                                  />
                                ))
                              ) : (
                                <Typography variant="caption" color="text.secondary">N/A</Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            <Box>
                              <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                Final: ₹{(quotation.finalAmount || 0)?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                              </Typography>
                              {quotation.paidAmount > 0 && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                                  Paid: ₹{(quotation.paidAmount || 0)?.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </Typography>
                              )}
                            </Box>
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            <Chip
                              label={getDisplayStatus(quotation)}  // Pass entire object
                              size="small"
                              color={getStatusChipColor(quotation.leadStatus || quotation.status)}  // Use leadStatus first
                              sx={{
                                fontWeight: "bold",
                                fontSize: "0.75rem",
                                height: "24px"
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}`, fontSize: '0.8rem' }}>
                            {formatDate(quotation.sentDate)}
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}`, fontSize: '0.8rem' }}>
                            {formatDate(quotation.validUntil)}
                          </TableCell>
                          <TableCell sx={{ borderRight: `1px solid ${alpha(primaryColor, 0.1)}` }}>
                            <Chip
                              label={getDisplayPaymentStatus(quotation.paymentStatus)}
                              size="small"
                              color={getPaymentStatusChipColor(quotation.paymentStatus)}
                              sx={{
                                fontSize: "0.75rem",
                                height: "24px"
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Box sx={{ display: "flex", gap: 0 }}>
                              <IconButton
                                size="small"
                                onClick={() => navigate(`/lead/${quotation.leadId}`)}
                                sx={{
                                  color: redcolor,
                                  "&:hover": { backgroundColor: alpha(redcolor, 0.1) },
                                  p: 0.4 // Slightly reduced padding
                                }}
                                title="View Lead"
                              >
                                <Visibility fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                onClick={() => handleOpenPDF(quotation)}
                                disabled={pdfLoading === quotation._id}
                                sx={{
                                  color: pdfLoading === quotation._id ? theme.palette.text.disabled : bluecolor,
                                  "&:hover": {
                                    backgroundColor: pdfLoading === quotation._id ? 'transparent' : alpha(bluecolor, 0.1)
                                  },
                                  p: 0.4 // Slightly reduced padding
                                }}
                                title={pdfLoading === quotation._id ? "Generating PDF..." : "View PDF"}
                              >
                                {pdfLoading === quotation._id ? (
                                  <CircularProgress size={20} sx={{ color: bluecolor }} />
                                ) : (
                                  <PictureAsPdf fontSize="small" />
                                )}
                              </IconButton>
                            </Box>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}
            </CardContent>
          </Card>
        </Box>
      </Box>

      {/* Filter Popover */}
      <Popover
        open={Boolean(filterAnchorEl)}
        anchorEl={filterAnchorEl}
        onClose={handleCloseFilterPopover}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
        transformOrigin={{ vertical: "top", horizontal: "left" }}
        PaperProps={{
          sx: {
            p: 2,
            minWidth: 300,
            maxWidth: 350,
            borderRadius: 2,
            border: `2px solid ${alpha(primaryColor, 0.1)}`,
            bgcolor: "background.paper",
            maxHeight: "500px", // Fixed overall height
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Typography variant="h6" gutterBottom sx={{ flexShrink: 0 }}>
          Filters
        </Typography>

        {/* Scrollable content container */}
        <Box
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 2,
            flex: 1,
            overflowY: "auto", // Scroll entire content if needed
            pr: 1, // Add padding for scrollbar
            "&::-webkit-scrollbar": {
              width: "6px",
            },
            "&::-webkit-scrollbar-track": {
              background: alpha(primaryColor, 0.05),
              borderRadius: "3px",
            },
            "&::-webkit-scrollbar-thumb": {
              background: alpha(primaryColor, 0.2),
              borderRadius: "3px",
              "&:hover": {
                background: alpha(primaryColor, 0.3),
              },
            },
          }}
        >
          {/* Status Filter - FIXED HEIGHT WITH INTERNAL SCROLL */}
          <FormControl fullWidth size="small" sx={{ flexShrink: 0, mt: 1 }}>
            <InputLabel>Quotation Status</InputLabel>
            <Select
              value={tempFilters.status}
              label="Quotation Status"
              onChange={(e) => setTempFilters({ ...tempFilters, status: e.target.value })}
              // MAKE SELECT DROPDOWN SCROLLABLE WITH FIXED HEIGHT
              MenuProps={{
                PaperProps: {
                  sx: {
                    maxHeight: 300, // Fixed height for dropdown
                    "& .MuiMenuItem-root": {
                      fontSize: "0.875rem", // Smaller font
                      py: 0.75, // Reduced padding
                    },
                    "& .MuiTypography-caption": {
                      pl: 1.5,
                      pt: 1,
                      pb: 0.5,
                      fontSize: "0.7rem",
                      fontWeight: 600,
                      color: theme.palette.text.secondary,
                      backgroundColor: alpha(primaryColor, 0.03),
                    }
                  }
                }
              }}
            >
              <MenuItem value="all">All Statuses</MenuItem>

              {/* Quotation Status Group - Optional grouping */}
              {/* <Typography variant="caption" sx={{ pl: 1.5, pt: 1, pb: 0.5 }}>
          Quotation Status
        </Typography> */}
              <MenuItem value="quotation_shared">Quotation Shared</MenuItem>
              <MenuItem value="quotation_approved">Quotation Approved</MenuItem>
              <MenuItem value="quotation_revised">Quotation Revised</MenuItem>
              <MenuItem value="quotation_rejected">Quotation Rejected</MenuItem>
              <MenuItem value="negotiation">Negotiation</MenuItem>
              <MenuItem value="expired">Expired</MenuItem>

              {/* Payment Status Group - Optional grouping */}
              {/* <Typography variant="caption" sx={{ pl: 1.5, pt: 1, pb: 0.5 }}>
          Payment Status
        </Typography> */}
              {/* <MenuItem value="token_paid">Token Paid</MenuItem> */}
              {/* <MenuItem value="partially_paid">Partially Paid</MenuItem> */}
              <MenuItem value="fully_paid">Fully Paid</MenuItem>
              <MenuItem value="overdue">Payment Overdue</MenuItem>
              <MenuItem value="pending">Payment Pending</MenuItem>
            </Select>
          </FormControl>

          {/* Company Filter for Super Admin */}
          {userRole === "superAdmin" && (
            <FormControl fullWidth size="small" sx={{ flexShrink: 0 }}>
              <InputLabel>Company</InputLabel>
              <Select
                value={tempFilters.company}
                label="Company"
                onChange={(e) => setTempFilters({ ...tempFilters, company: e.target.value })}
                // FIXED HEIGHT FOR COMPANY DROPDOWN TOO
                MenuProps={{
                  PaperProps: {
                    sx: {
                      maxHeight: 250,
                    }
                  }
                }}
              >
                <MenuItem value="all">All Companies</MenuItem>
                {companies.map((company) => (
                  <MenuItem key={company._id} value={company._id}>
                    {company.companyName || company.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* Fixed buttons at bottom - ALWAYS VISIBLE */}
        <Box
          sx={{
            display: "flex",
            gap: 1,
            justifyContent: "flex-end",
            mt: 2,
            pt: 2,
            borderTop: `1px solid ${alpha(primaryColor, 0.1)}`,
            flexShrink: 0,
          }}
        >
          <Button onClick={handleResetFilters} color="inherit" size="small">
            Reset
          </Button>
          <Button
            variant="contained"
            onClick={handleApplyFilters}
            size="small"
            sx={{
              bgcolor: "primary.main",
              "&:hover": {
                bgcolor: alpha(primaryColor, 0.9),
              },
            }}
          >
            Apply
          </Button>
        </Box>
      </Popover>

      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />
    </>
  );
};

export default QuotationsScreen;